import React, {
  CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Tooltip as MUITooltip,
  tooltipClasses,
  TooltipProps,
} from '@mui/material';
import styled from 'styled-components';
import { ITooltipTemplateProps } from './types';
import { ExtendedTooltipTitle } from './components/ExtendedTitle';
import { TooltipTitle } from './components/Title';
import { IProductResponse } from 'utils/types';

const HTMLTooltip = styled(
  ({
    className,
    templateTooltipStyle,
    ...props
  }: TooltipProps & { templateTooltipStyle?: CSSProperties }) => (
    <MUITooltip
      {...props}
      classes={{ popper: className }}
      style={{
        cursor: 'pointer',
      }}
      placement="bottom-end"
    >
      {props.children}
    </MUITooltip>
  ),
)(({ templateTooltipStyle }) => {
  if (templateTooltipStyle)
    return {
      [`& .${tooltipClasses.tooltip}`]: templateTooltipStyle,
    };
});

const getProductsSrcMap = (
  products: IProductResponse[],
): Record<string, string> => {
  if (!products || !products.length) return {};

  return products.reduce((accumulator, { publisherProductId, images }) => {
    const img = images.find(({ type }) => type === 'product')?.url;

    return img ? { ...accumulator, [publisherProductId]: img } : accumulator;
  }, {});
};

const TOOLTIP_TIMER = 4000;

export const TooltipTemplate: React.FC<ITooltipTemplateProps> = ({
  children,
  content,
  templateTooltipStyle = {},
  buttonStyle = {},
  headersColorCss,
  dividerColor = '#fff',
  tooltipOpenEventHandler,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [arrProductIds, setArrProductIds] = useState<string[]>([]);
  const [mapProductSrc, setMapProductSrc] = useState<Record<string, string>>(
    {},
  );

  const { title, titleImg, sections, tooltipLoadCallback } = useMemo(() => {
    if (
      typeof content === 'string' ||
      !content ||
      content.sections.length === 0
    )
      return {
        title: content,
        titleImg: null,
        sections: null,
        tooltipLoadCallback: null,
      };
    return content;
  }, [content]);

  const toggleTooltip = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (isOpen) {
      tooltipOpenEventHandler?.();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!sections || sections.length === 0) return;

    const arrIds = new Set<string>();
    sections.map(({ products }) =>
      products.map(({ publisherProductId }) => arrIds.add(publisherProductId)),
    );
    setArrProductIds(Array.from(arrIds));
  }, [sections]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isOpen) {
      timer = setTimeout(() => {
        setIsOpen(false);
      }, TOOLTIP_TIMER);
    }
    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && sections) {
      if (tooltipLoadCallback && arrProductIds.length > 0) {
        tooltipLoadCallback(arrProductIds)
          .then(getProductsSrcMap)
          .then(setMapProductSrc);
      } else {
        setMapProductSrc({});
      }
    }
  }, [isOpen, sections, arrProductIds]);

  return (
    <HTMLTooltip
      title={
        sections ? (
          <ExtendedTooltipTitle
            title={title}
            titleImg={titleImg}
            sections={sections}
            mapProductSrc={mapProductSrc}
            headersColorCss={headersColorCss}
            dividerColor={dividerColor}
          />
        ) : (
          <TooltipTitle text={title as string} />
        )
      }
      open={isOpen}
      onClose={() => setIsOpen(false)}
      templateTooltipStyle={templateTooltipStyle}
      disableHoverListener
      disableFocusListener
      disableTouchListener
    >
      <div ref={tooltipRef} onClick={toggleTooltip} style={buttonStyle}>
        {children}
      </div>
    </HTMLTooltip>
  );
};
